/* eslint-disable no-continue */
import * as React from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import {
    Grid,
    IconButton,
    LinearProgress,
    Stack,
    Typography,
} from '@mui/material';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import { ManagePacketService } from 'services';
import {
    downloadCSV,
    downloadPDF,
    downloadZip,
} from 'shared/helpers/DirectDownload';
import JSZip from 'jszip';
import FileSaver from 'file-saver';
import { areWeTestingWithJest } from 'test/helpers/formik';
import CloseIcon from '@mui/icons-material/Close';
import { Data } from './VirtualizedTable';
import { useTimeoutFn } from 'react-use';

import { formattingAction } from 'shared/helpers/FormatAction';
import { Utils } from 'shared/helpers';
import { typeMap } from './data/typeMap';

const BulkModal: React.FC<Props> = React.forwardRef(
    (
        { bulkData: _bulkData, callbackClosingModal, callbackOpenModal },
        ref
    ): React.ReactElement => {
        const [isGeneratingZip, setGenerattingZip] =
            React.useState<boolean>(false);
        const [isDownloading, setDownloading] = React.useState<boolean>(false);
        const [isProgress, setProgress] = React.useState<boolean>(false);
        const [isDownloadingAudit, setDownloadingAudit] =
            React.useState<boolean>(false);
        const [isDownloadingPreferred, setDownloadingPreferred] =
            React.useState<boolean>(false);
        const [isLoading, setIsLoading] = React.useState<boolean>(true);
        const [bulkData, setBulkData] = React.useState<any>(null);
        const arrayData = React.useMemo(
            () =>
                bulkData
                    ? Array.from(Object.keys(bulkData)).map((el) =>
                          bulkData[el].placeholder
                              ? String(bulkData[el].placeholder)
                                    .replace('.zip', '')
                                    .replace('.ZIP', '')
                              : el
                      )
                    : null,
            [bulkData]
        );

        const [arrayDataFailureDownloads, setArrayFailureDownloads] =
            React.useState<boolean[]>([]);
        const [arrayDataSuccessDownloads, setArraySuccessDownloads] =
            React.useState<boolean[]>([]);
        const [arrayDataFailureAudtis, setArrayFailureAudits] = React.useState<
            boolean[]
        >([]);
        const [arrayDataSuccessAudits, setArraySuccessAudits] = React.useState<
            boolean[]
        >([]);
        const [arrayDataFailureReports, setArrayFailureReports] =
            React.useState<boolean[]>([]);
        const [arrayDataSuccessReports, setArraySuccessReports] =
            React.useState<boolean[]>([]);
        const [arrayDataFailurePreferred, setArrayFailurePreferred] =
            React.useState<boolean[]>([]);
        const [arrayDataSuccessPreferred, setArraySuccessPreferred] =
            React.useState<boolean[]>([]);
        const [open, setOpen] = React.useState(false);

        React.useEffect(() => {
            if (open) {
                const prefetchSinglePacket: Promise<any>[] = Array.from(
                    Object.keys(_bulkData ?? {})
                ).map(
                    (el) =>
                        new Promise((resolve, reject) => {
                            ManagePacketService.getSinglePackets(el)
                                .then(resolve)
                                .catch(reject);
                        })
                );

                Promise.all(prefetchSinglePacket)
                    .then((e) => {
                        const updatedBulkData: BulkData = {};
                        // eslint-disable-next-line no-plusplus
                        for (let index = 0; index < e.length; index++) {
                            const element = e[index];
                            const response = element.data.data;
                            const data: Data = {
                                ...response,
                                actions: {
                                    type:
                                        response.status === ''
                                            ? typeMap.notExpected?.[0]
                                            : typeMap[response.status]?.[0],
                                    ...formattingAction(response),
                                },
                                bin: response.binNumber,
                                clientName: response.clientName,
                                completedDate: response.completedDate,
                                completedDateDifference:
                                    response.completedDateDifference,
                                escalated: response.escalated,
                                escalatedDate: response.escalatedDate,
                                escalatedAtDifference:
                                    response.ecalatedAtDifference,
                                escalatedByRole: response.escalatedByRole,
                                loanNumber: response.loanNumber,
                                operatorId: response.createdBy,
                                cm9Number: response.cm9Number,
                                packetStatus:
                                    response.status === ''
                                        ? typeMap.notExpected?.[1]
                                        : typeMap[response.status]?.[1],
                                packetType: response.type,
                                recordNo: response.uniqueRef,
                                reason: response.statusMessage,
                                archiveDateDifference:
                                    response.archiveDateDifference,
                                archiveTill: response.archiveTill,
                                SLADate: response.slaDate,
                                uploadedDate: response.createdAt,
                                documents: response.documents,
                                clientId: response.clientId,
                                createdAtDifference:
                                    response.createdAtDifference,
                                expiryDate: response.expiryDate,
                                expiryDateDifference:
                                    response.expiryDateDifference,
                                reportFile: response.reportFile,
                                reviewRequired: response.reviewRequired,
                                slaDateDifference: response.slaDateDifference,
                                updatedAtDifference:
                                    response.updatedAtDifference,
                                updatedAt: response.updatedAt,
                                file: response.file,
                                isOpen: response.operatorAction.open,
                                openedBy: response.operatorAction.openedBy,
                                enableAuditDownload:
                                    response.enableAuditDownload,
                                enableDocumentDownload:
                                    response.enableDocumentDownload,
                                highlighted: response.highlighted,
                                packetAuditFile: response.packetAuditFile,
                                packetAuditFileCSV: response.packetAuditFileCSV,
                                cm9Timer: response.cm9Timer,
                                preferredDocuments: response.preferredDocuments,
                                placeholder: response.placeholder,
                                updateCategory: response.updateCategory,
                                showCategory: response.showCategory,
                                documentsRequired: response.documentsRequired,
                                expectedMissingDocuments:
                                    response.expectedMissingDocuments,
                                operatorName: response.operatorName,
                            };
                            // eslint-disable-next-line no-underscore-dangle
                            updatedBulkData[data.actions.uuid] = data;
                        }
                        setBulkData(updatedBulkData);
                        setIsLoading(false);
                    })
                    .catch((response) => {
                        Utils.handleErrorResponse(response);
                        setOpen(false);
                        setIsLoading(true);
                    });
            } else {
                setIsLoading(true);
            }
        }, [open]);

        const handleOpen = React.useCallback((): void => {
            setOpen(true);
            if (typeof callbackOpenModal === 'function') callbackOpenModal();
        }, []);

        const handleClose = React.useCallback((): void => {
            if (
                !isGeneratingZip &&
                !isDownloading &&
                !isDownloadingAudit &&
                !isDownloadingPreferred
            ) {
                setOpen(false);
                if (typeof callbackClosingModal === 'function') {
                    const prefetchSinglePacket: string[] = Array.from(
                        Object.keys(_bulkData ?? {})
                    ).map((el) => el);
                    callbackClosingModal(prefetchSinglePacket);
                }
            }
        }, [
            _bulkData,
            callbackClosingModal,
            isDownloading,
            isDownloadingAudit,
            isDownloadingPreferred,
            isGeneratingZip,
        ]);
        React.useImperativeHandle(
            ref,
            () => ({
                setClose: () => {
                    handleClose();
                },
                setOpen: () => {
                    handleOpen();
                },
            }),
            [handleOpen, handleClose]
        );
        const [downloadCount, setDownloadCount] = React.useState<
            Array<boolean>
        >([]);
        const handleProgressBarTimeout = React.useCallback(() => {
            setProgress(false);
            setArrayFailureAudits([]);
            setArraySuccessAudits([]);
            setArrayFailureDownloads([]);
            setArraySuccessDownloads([]);
            setArrayFailurePreferred([]);
            setArraySuccessPreferred([]);
            setArrayFailureReports([]);
            setArraySuccessReports([]);
            setDownloadCount([]);
        }, []);

        const [isReady, cancel, reset] = useTimeoutFn(
            handleProgressBarTimeout,
            5000
        );
        const handleProgressBarReset = React.useCallback(() => {
            setProgress(true);
            if (isReady() === false) {
                cancel();
                setDownloadCount([]);
                setArrayFailureAudits([]);
                setArraySuccessAudits([]);
                setArrayFailureDownloads([]);
                setArraySuccessDownloads([]);
                setArrayFailurePreferred([]);
                setArraySuccessPreferred([]);
                setArrayFailureReports([]);
                setArraySuccessReports([]);
            }
        }, [cancel, isReady]);
        React.useLayoutEffect(() => {
            if (
                !isDownloading &&
                !isDownloadingPreferred &&
                !isDownloadingAudit &&
                !isGeneratingZip
            ) {
                reset();
            }
        }, [
            isDownloading,
            isDownloadingPreferred,
            isDownloadingAudit,
            isGeneratingZip,
        ]);

        const [isUpdatingDownloadCount, setUpdatingDownloadCount] =
            React.useState<boolean>(false);
        React.useLayoutEffect(() => {
            if (isDownloading) {
                const count = downloadCount.at(0);
                if (count !== true) {
                    const updatedCount = downloadCount;
                    downloadCount[0] = true;
                    setDownloadCount(updatedCount);
                    setUpdatingDownloadCount(true);
                }
            }
        }, [isDownloading]);
        React.useLayoutEffect(() => {
            if (isDownloadingPreferred) {
                const count = downloadCount.at(1);
                if (count !== true) {
                    const updatedCount = downloadCount;
                    downloadCount[1] = true;
                    setDownloadCount(updatedCount);
                    setUpdatingDownloadCount(true);
                }
            }
        }, [isDownloadingPreferred]);
        React.useLayoutEffect(() => {
            if (isDownloadingAudit) {
                const count = downloadCount.at(2);
                if (count !== true) {
                    const updatedCount = downloadCount;
                    downloadCount[2] = true;
                    setDownloadCount(updatedCount);
                    setUpdatingDownloadCount(true);
                }
            }
        }, [isDownloadingAudit]);
        React.useLayoutEffect(() => {
            if (isGeneratingZip) {
                const count = downloadCount.at(3);
                if (count !== true) {
                    const updatedCount = downloadCount;
                    downloadCount[3] = true;
                    setDownloadCount(updatedCount);
                    setUpdatingDownloadCount(true);
                }
            }
        }, [isGeneratingZip]);
        React.useLayoutEffect(() => {
            if (isUpdatingDownloadCount) {
                setUpdatingDownloadCount(false);
            }
        }, [isUpdatingDownloadCount]);
        React.useLayoutEffect(() => {
            cancel();
        }, []);
        // const getReducementInProcessedDocument = React.useCallback(
        //     (prev: any[], current: any) => {
        //         const arrayDocument = prev;
        //         if (
        //             arrayDocument.some(
        //                 (i) => i.placeholder === current.placeholder
        //             ) === false
        //         ) {
        //             arrayDocument.push(current);
        //         }

        //         return arrayDocument;
        //     },
        //     []
        // );
        const handleReportFilename = React.useCallback(
            (filename: string, type: string) =>
                filename?.split('/').find((segment) => segment.includes(type)),
            []
        );
        const handleAuditFilename = React.useCallback(
            (url: URL, type: string) =>
                url.pathname
                    ?.split('/')
                    .find((segment) => segment.includes(type)),
            []
        );
        const createArchiveDraftInLocalStorage = React.useCallback(
            (
                bulkedData: BulkData,
                options: string,
                callbackSuccess: (index: number) => void,
                callbackError: (index: number) => void
            ): Promise<BulkDrafted> =>
                new Promise(async (resolve) => {
                    const BulkDrafted: BulkDrafted = {};
                    const bulkDataKey = Object.keys(bulkedData);
                    // eslint-disable-next-line no-plusplus
                    for (let index = 0; index < bulkDataKey.length; index++) {
                        const key = bulkDataKey[index];

                        const BulkValue = bulkedData[key];
                        const bulkAuditComplementary =
                            BulkValue.packetStatus === 'Completed' &&
                            typeof BulkValue.packetAuditFile === 'string' &&
                            typeof BulkValue.packetAuditFileCSV === 'string';
                        const bulkAuditPDFCompliance =
                            BulkValue.packetStatus === 'Completed' &&
                            typeof BulkValue.packetAuditFile === 'string';
                        BulkDrafted[key] = {
                            documents: null,
                            reportFile: null,
                            actions: BulkValue.actions,
                            csvAuditFile: null,
                            pdfAuditFile: null,
                            placeholder: BulkValue.placeholder
                                ? String(BulkValue.placeholder)
                                      .replace('.zip', '')
                                      .replace('.ZIP', '')
                                : BulkValue.actions.uuid,
                            preferred: [],
                        };

                        if (options === 'audit') {
                            if (
                                BulkValue.enableAuditDownload === true &&
                                bulkAuditComplementary === false
                            ) {
                                const responseAudit =
                                    // eslint-disable-next-line no-await-in-loop
                                    await ManagePacketService.getPacketAuditfile(
                                        {
                                            packetId: BulkValue.actions.uuid,
                                        }
                                    )
                                        .then((response: any) => {
                                            const {
                                                csvAuditFile,
                                                pdfAuditFile,
                                            } = response.data.data;
                                            return {
                                                csvAuditFile,
                                                pdfAuditFile,
                                            };
                                        })
                                        .catch(() => {
                                            callbackError(index);
                                            return null;
                                        });
                                if (responseAudit) {
                                    BulkDrafted[key].csvAuditFile =
                                        responseAudit.csvAuditFile;
                                    BulkDrafted[key].pdfAuditFile =
                                        responseAudit.pdfAuditFile;
                                } else {
                                    callbackError(index);
                                    continue;
                                }
                            } else if (
                                BulkValue.enableAuditDownload === true &&
                                bulkAuditComplementary === true
                            ) {
                                BulkDrafted[key].csvAuditFile =
                                    BulkValue.packetAuditFileCSV;
                                BulkDrafted[key].pdfAuditFile =
                                    BulkValue.packetAuditFile;
                            } else {
                                callbackError(index);
                                continue;
                            }
                        } else if (options === 'reports') {
                            if (BulkValue.enableAuditDownload === true) {
                                BulkDrafted[key].reportFile =
                                    BulkValue.reportSAS;
                            } else {
                                callbackError(index);
                                continue;
                            }
                        } else if (options === 'documents') {
                            if (
                                BulkValue.enableDocumentDownload === true &&
                                BulkValue.placeholder !== null
                            ) {
                                BulkDrafted[key].documents = {
                                    filePlaceholder: BulkValue.placeholder,
                                    documents: BulkValue.documentZipSAS,
                                };
                            } else {
                                callbackError(index);
                                continue;
                            }
                        } else if (options === 'preferred') {
                            if (BulkValue.enableDocumentDownload === true) {
                                if (
                                    bulkAuditPDFCompliance ||
                                    bulkAuditComplementary
                                ) {
                                    const pdfAuditFilename =
                                        handleAuditFilename(
                                            new window.URL(
                                                BulkValue.packetAuditFile
                                            ),
                                            '.pdf'
                                        );
                                    const processedAuditFile = [];
                                    if (pdfAuditFilename) {
                                        processedAuditFile.push({
                                            fileSasToken:
                                                BulkValue.packetAuditFile,
                                            processedFile:
                                                BulkValue.packetAuditFile,
                                            placeholder: pdfAuditFilename,
                                            category: 'report',
                                            type: 'pdf',
                                        });
                                    }

                                    BulkDrafted[key].preferred =
                                        BulkValue.preferredDocuments
                                            .concat([
                                                {
                                                    fileSasToken:
                                                        BulkValue.reportSAS,
                                                    processedFile:
                                                        BulkValue.reportFile,
                                                    placeholder:
                                                        handleReportFilename(
                                                            BulkValue.reportFile,
                                                            '.pdf'
                                                        ) ?? '',
                                                    category: 'report',
                                                },
                                            ])
                                            .concat(processedAuditFile);
                                } else {
                                    const responseAudit =
                                        // eslint-disable-next-line no-await-in-loop
                                        await ManagePacketService.getPacketAuditfile(
                                            {
                                                packetId:
                                                    BulkValue.actions.uuid,
                                            }
                                        )
                                            .then((response: any) => {
                                                const {
                                                    csvAuditFile,
                                                    pdfAuditFile,
                                                } = response.data.data;
                                                return {
                                                    csvAuditFile,
                                                    pdfAuditFile,
                                                };
                                            })
                                            .catch(() => {
                                                callbackError(index);
                                                return null;
                                            });
                                    const processedAuditFile = [];

                                    if (responseAudit) {
                                        // const csvAuditFilename =
                                        //     handleAuditFilename(
                                        //         new window.URL(
                                        //             responseAudit.csvAuditFile
                                        //         ),
                                        //         '.csv'
                                        //     );
                                        const pdfAuditFilename =
                                            handleAuditFilename(
                                                new window.URL(
                                                    responseAudit.pdfAuditFile
                                                ),
                                                '.pdf'
                                            );

                                        // if (csvAuditFilename) {
                                        //     processedAuditFile.push({
                                        //         processedFile:
                                        //             responseAudit.csvAuditFile,
                                        //         placeholder: csvAuditFilename,
                                        //         category: 'report',
                                        //         type: 'csv',
                                        //     });
                                        // }
                                        if (pdfAuditFilename) {
                                            processedAuditFile.push({
                                                fileSasToken:
                                                    responseAudit.pdfAuditFile,
                                                processedFile:
                                                    responseAudit.pdfAuditFile,
                                                placeholder: pdfAuditFilename,
                                                category: 'report',
                                                type: 'pdf',
                                            });
                                        }
                                        BulkDrafted[key].preferred =
                                            BulkValue.preferredDocuments
                                                .concat([
                                                    {
                                                        fileSasToken:
                                                            BulkValue.reportSAS,
                                                        processedFile:
                                                            BulkValue.reportFile,
                                                        placeholder:
                                                            handleReportFilename(
                                                                BulkValue.reportFile,
                                                                '.pdf'
                                                            ) ?? '',
                                                        category: 'report',
                                                    },
                                                ])
                                                .concat(processedAuditFile);
                                    } else {
                                        callbackError(index);
                                        continue;
                                    }
                                }
                            } else {
                                callbackError(index);
                                continue;
                            }
                        }
                        callbackSuccess(index);
                    }

                    resolve(BulkDrafted);
                }),
            [handleAuditFilename, handleReportFilename]
        );
        /**
         * get all pending docs using SaS service
         *
         * @param pendingDocs - "string" array of url document to generate signed url.
         * @returns {Promise<{ [key: string]: JSZipObject }>} A promise that resolves when all files is signed within SaS service.
         */
        const getPendingFileOrCSVFromSasService = React.useCallback<
            (pendingDocs: string[]) => Promise<{
                data: { data: { url: string } };
            }>[]
        >(
            (pendingDocs: string[]) =>
                Array.from(pendingDocs).map(
                    (el) =>
                        new Promise(async (resolve, reject) => {
                            await ManagePacketService.getSasToken({
                                fileName: el,
                                report: false,
                            })
                                .then(resolve)
                                .catch(reject);
                        })
                ),
            []
        );
        /**
         * get all pending CSV using fetch download
         *
         * @param pendingDocs - "string" array of url document to download.
         * @returns {Promise<Blob>} A promise that resolves when all zip files are downloaded.
         */
        const getPendingCSV = React.useCallback<
            (pendingDocs: string[]) => Promise<Blob>[]
        >(
            (pendingZips: string[]) =>
                Array.from(pendingZips).map(
                    (el) =>
                        new Promise(async (resolve, reject) => {
                            downloadCSV(el).then(resolve).catch(reject);
                        })
                ),
            []
        );
        /**
         * get all pending PDF using fetch download
         *
         * @param pendingDocs - "string" array of url document to download.
         * @returns {Promise<Blob>} A promise that resolves when all zip files are downloaded.
         */
        const getPendingPDF = React.useCallback<
            (pendingDocs: string[]) => Promise<Blob>[]
        >(
            (pendingZips: string[]) =>
                Array.from(pendingZips).map(
                    (el) =>
                        new Promise(async (resolve, reject) => {
                            downloadPDF(el).then(resolve).catch(reject);
                        })
                ),
            []
        );

        /**
         * get pending ZIP using fetch download
         *
         * @param pendingDocs - "string" array of url document to download.
         * @returns {Promise<Blob>} A promise that resolves when all zip files are downloaded.
         */
        const getPendingZIP = React.useCallback<
            (
                pendingZip: string
            ) => Promise<{ [key: string]: JSZip.JSZipObject }>
        >(
            (pendingZip: string) =>
                new Promise(async (resolve, reject) => {
                    downloadZip(pendingZip).then(resolve).catch(reject);
                }),
            []
        );

        const dataURLtoBlob = (dataurl: string): Blob => {
            const arr = dataurl.split(',');
            const mime = arr[0].match(/:(.*?);/);
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            // eslint-disable-next-line no-plusplus
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            return new Blob([u8arr], { type: mime !== null ? mime[1] : '' });
        };
        const blobToDataURL = (blob: Blob): Promise<string> =>
            new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = () => reject(reader.error);
                reader.onabort = () => reject(new Error('Read aborted'));
                reader.readAsDataURL(blob);
            });

        const walkArchiveDraftInLocalStorage = React.useCallback(
            async (
                valueDraft: BulkDrafted,
                callbackSuccess: (index: number) => void,
                callbackError: (index: number) => void
            ): Promise<BulkWalk> =>
                new Promise(async (resolve) => {
                    if (valueDraft) {
                        const BulkDrafted: BulkDrafted = valueDraft;
                        const BulkWalk: BulkWalk = {};
                        const bulkDraftedKey = Object.keys(BulkDrafted);
                        for (
                            let index = 0;
                            index < bulkDraftedKey.length;
                            // eslint-disable-next-line no-plusplus
                            index++
                        ) {
                            const key = bulkDraftedKey[index];

                            const BulkValue = BulkDrafted[key];
                            BulkWalk[key] = {
                                documents: {
                                    files: [],
                                    placeholder: [],
                                },
                                reportFile: {
                                    file: '',
                                    placeholder: '',
                                },
                                actions: BulkValue.actions,
                                csvAuditFile: '',
                                placeholderCSVAuditFilename: '',
                                pdfAuditFile: '',
                                placeholderPDFAuditFilename: '',
                                placeholder: BulkValue.placeholder,
                                preferred: [],
                            };
                            if (
                                BulkValue.documents !== null &&
                                BulkValue.documents.filePlaceholder
                            ) {
                                try {
                                    if (
                                        BulkValue.documents !== null &&
                                        BulkValue.documents.documents &&
                                        BulkValue.documents.documents.length > 0
                                    ) {
                                        const arrayPendingZip =
                                            BulkValue.documents.documents;
                                        const PendingJSZIPFile =
                                            // eslint-disable-next-line no-await-in-loop
                                            await getPendingZIP(
                                                arrayPendingZip
                                            );

                                        const ArrayZipFiles =
                                            // eslint-disable-next-line no-await-in-loop
                                            Object.keys(PendingJSZIPFile);
                                        // create new zip when there is processedFile
                                        const serializedBlob: string[] = [];
                                        const placeholderBlob: string[] = [];

                                        for (
                                            let e = 0;
                                            e < ArrayZipFiles.length;
                                            // eslint-disable-next-line no-plusplus
                                            e++
                                        ) {
                                            const filename = ArrayZipFiles[e];
                                            const filecontent =
                                                PendingJSZIPFile[filename];
                                            const asyncFileContent =
                                                // eslint-disable-next-line no-await-in-loop
                                                await filecontent.async('blob');
                                            const elementName =
                                                filecontent.name;

                                            const stringifyBlob =
                                                // eslint-disable-next-line no-await-in-loop
                                                await blobToDataURL(
                                                    asyncFileContent
                                                );

                                            serializedBlob.push(stringifyBlob);

                                            placeholderBlob.push(elementName);
                                        }
                                        BulkWalk[key].documents.files =
                                            serializedBlob;
                                        BulkWalk[key].documents.placeholder =
                                            placeholderBlob;
                                    }
                                    callbackSuccess(index);
                                } catch (error) {
                                    callbackError(index);
                                }
                            }
                            if (
                                Array.isArray(BulkValue.preferred) &&
                                BulkValue.preferred.length > 0
                            ) {
                                const filterPreferred =
                                    BulkValue.preferred.filter(
                                        (e: any) =>
                                            e.fileSasToken !== null &&
                                            typeof e.type === 'undefined'
                                    );
                                const filterPreferredAudit =
                                    BulkValue.preferred.filter(
                                        (e: any) =>
                                            e.fileSasToken !== null &&
                                            typeof e.type === 'string'
                                    );
                                try {
                                    const arrayOfDownloadPDF: Array<
                                        Blob | undefined
                                    > =
                                        // eslint-disable-next-line no-await-in-loop
                                        await Promise.all(
                                            filterPreferred
                                                .map(
                                                    // eslint-disable-next-line no-loop-func
                                                    (
                                                        obj: any
                                                    ):
                                                        | Promise<Blob>
                                                        | undefined =>
                                                        new Promise(
                                                            async (
                                                                resolve,
                                                                reject
                                                            ) => {
                                                                downloadPDF(
                                                                    obj.fileSasToken
                                                                )
                                                                    .then(
                                                                        resolve
                                                                    )
                                                                    .catch(
                                                                        reject
                                                                    );
                                                            }
                                                        )
                                                )
                                                .concat(
                                                    filterPreferredAudit.map(
                                                        (e: any) => {
                                                            if (
                                                                e.type === 'csv'
                                                            ) {
                                                                const promisePendingCSV =
                                                                    getPendingCSV(
                                                                        [
                                                                            e.fileSasToken,
                                                                        ]
                                                                    );
                                                                return promisePendingCSV.at(
                                                                    0
                                                                );
                                                            }
                                                            const promisePendingDocs =
                                                                getPendingPDF([
                                                                    e.fileSasToken,
                                                                ]);
                                                            return promisePendingDocs.at(
                                                                0
                                                            );
                                                        }
                                                    )
                                                )
                                        ).catch(() => []);

                                    const filteredPreferredOrigin =
                                        BulkValue.preferred.filter(
                                            (e: any) => e.fileSasToken !== null
                                        );

                                    const pendingBlobFromDocs =
                                        // eslint-disable-next-line no-await-in-loop
                                        await Promise.all<Blob | undefined>(
                                            arrayOfDownloadPDF
                                        );

                                    if (
                                        pendingBlobFromDocs &&
                                        Array.isArray(pendingBlobFromDocs) &&
                                        pendingBlobFromDocs.length > 0
                                    ) {
                                        for (
                                            let i = 0;
                                            i < filteredPreferredOrigin.length;
                                            // eslint-disable-next-line no-plusplus
                                            i++
                                        ) {
                                            const preferred =
                                                filteredPreferredOrigin[i];
                                            const blob = pendingBlobFromDocs[i];
                                            if (typeof blob !== 'undefined') {
                                                const preferredDataUrl =
                                                    // eslint-disable-next-line no-await-in-loop
                                                    await blobToDataURL(blob);
                                                callbackSuccess(index);
                                                BulkWalk[key].preferred[i] = {
                                                    file: preferredDataUrl,
                                                    placeholder:
                                                        preferred.placeholder,
                                                };
                                            }
                                        }
                                    } else {
                                        callbackError(index);
                                    }
                                } catch (error) {
                                    callbackError(index);
                                }
                            }
                            if (BulkValue.reportFile) {
                                // eslint-disable-next-line no-await-in-loop
                                try {
                                    const downloadUrl = BulkValue.reportFile;
                                    const fileName: any = BulkValue.reportFile
                                        .split('/')
                                        .pop();
                                    const cleanFilename = String(
                                        fileName
                                    ).replace('.zip_', '_');
                                    // eslint-disable-next-line no-await-in-loop
                                    const fileBlob = await downloadPDF(
                                        downloadUrl
                                    );
                                    // eslint-disable-next-line no-await-in-loop
                                    const stringifyBlob = await blobToDataURL(
                                        fileBlob
                                    );
                                    BulkWalk[key].reportFile.file =
                                        stringifyBlob;
                                    BulkWalk[key].reportFile.placeholder =
                                        cleanFilename;
                                    callbackSuccess(index);
                                } catch (error) {
                                    callbackError(index);
                                }
                            }
                            if (BulkValue.csvAuditFile) {
                                try {
                                    // eslint-disable-next-line no-await-in-loop

                                    // eslint-disable-next-line no-await-in-loop
                                    const fileBlob = await downloadCSV(
                                        BulkValue.csvAuditFile
                                    );
                                    const placeholder = handleAuditFilename(
                                        new window.URL(BulkValue.csvAuditFile),
                                        '.csv'
                                    );
                                    // eslint-disable-next-line no-await-in-loop
                                    const stringifyBlob = await blobToDataURL(
                                        fileBlob
                                    );
                                    BulkWalk[key].csvAuditFile = stringifyBlob;
                                    BulkWalk[key].placeholderCSVAuditFilename =
                                        placeholder ?? null;
                                    callbackSuccess(index);
                                } catch (error) {
                                    callbackError(index);
                                }
                            }
                            if (BulkValue.pdfAuditFile) {
                                try {
                                    // eslint-disable-next-line no-await-in-loop
                                    const fileBlob = await downloadPDF(
                                        BulkValue.pdfAuditFile
                                    );
                                    const placeholder = handleAuditFilename(
                                        new window.URL(BulkValue.pdfAuditFile),
                                        '.pdf'
                                    );
                                    // eslint-disable-next-line no-await-in-loop
                                    const stringifyBlob = await blobToDataURL(
                                        fileBlob
                                    );

                                    BulkWalk[key].pdfAuditFile = stringifyBlob;
                                    BulkWalk[key].placeholderPDFAuditFilename =
                                        placeholder ?? null;
                                    callbackSuccess(index);
                                } catch (error) {
                                    callbackError(index);
                                }
                            }
                        }

                        resolve(BulkWalk);
                    }
                }),
            [
                getPendingCSV,
                getPendingFileOrCSVFromSasService,
                getPendingPDF,
                handleAuditFilename,
            ]
        );
        const downloadArchiveDraftForActionDownloads = React.useCallback(
            (valueWalk: BulkWalk): Promise<void> =>
                new Promise((resolve) => {
                    if (valueWalk) {
                        const bulkWalkedKey = Object.keys(valueWalk);
                        // create new zip
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const zip = new JSZip();

                        // eslint-disable-next-line no-plusplus
                        for (
                            let index = 0;
                            index < bulkWalkedKey.length;
                            // eslint-disable-next-line no-plusplus
                            index++
                        ) {
                            const key = bulkWalkedKey[index];

                            const BulkValue = valueWalk[key];
                            const folderzip = zip.folder(BulkValue.placeholder);
                            if (BulkValue.documents.files) {
                                BulkValue.documents.files.forEach((el, i) => {
                                    const placeholder =
                                        BulkValue.documents.placeholder[i];

                                    folderzip?.file(
                                        placeholder,
                                        dataURLtoBlob(el)
                                    );
                                });
                            }
                        }

                        zip.generateAsync({ type: 'blob' }).then((content) => {
                            // see FileSaver.js
                            FileSaver.saveAs(
                                content,
                                'Downloaded Documents Cross Check Data'
                            );
                        });
                    }
                    resolve();
                }),
            []
        );
        const downloadArchiveDraftForActionReports = React.useCallback(
            (valueWalk: BulkWalk): Promise<void> =>
                new Promise((resolve) => {
                    if (valueWalk) {
                        const BulkWalked: BulkWalk = valueWalk;
                        const bulkWalkedKey = Object.keys(BulkWalked);

                        // create new zip
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const zip = new JSZip();
                        // eslint-disable-next-line no-plusplus
                        for (
                            let index = 0;
                            index < bulkWalkedKey.length;
                            // eslint-disable-next-line no-plusplus
                            index++
                        ) {
                            const key = bulkWalkedKey[index];

                            const BulkValue = BulkWalked[key];
                            const folderzip = zip.folder(BulkValue.placeholder);
                            if (
                                BulkValue.reportFile &&
                                BulkValue.reportFile.file
                            ) {
                                folderzip?.file(
                                    BulkValue.reportFile.placeholder,
                                    dataURLtoBlob(BulkValue.reportFile.file)
                                );
                            }
                        }

                        zip.generateAsync({ type: 'blob' }).then((content) => {
                            // see FileSaver.js
                            FileSaver.saveAs(
                                content,
                                'Downloaded Reports Cross Check Data'
                            );
                        });
                    }
                    resolve();
                }),
            []
        );
        const downloadArchiveDraftForActionAudits = React.useCallback(
            (valueWalk: BulkWalk): Promise<void> =>
                new Promise((resolve) => {
                    if (valueWalk) {
                        const BulkWalked: BulkWalk = valueWalk;
                        const bulkWalkedKey = Object.keys(BulkWalked);
                        // create new zip
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const zip = new JSZip();

                        // eslint-disable-next-line no-plusplus
                        for (
                            let index = 0;
                            index < bulkWalkedKey.length;
                            // eslint-disable-next-line no-plusplus
                            index++
                        ) {
                            const key = bulkWalkedKey[index];

                            const BulkValue = BulkWalked[key];
                            const folderzip = zip.folder(BulkValue.placeholder);
                            if (BulkValue.csvAuditFile) {
                                folderzip?.file(
                                    BulkValue.placeholderCSVAuditFilename !==
                                        null
                                        ? BulkValue.placeholderCSVAuditFilename
                                        : `${BulkValue.actions.uuid}.csv`,
                                    dataURLtoBlob(BulkValue.csvAuditFile)
                                );
                            }
                            if (BulkValue.pdfAuditFile) {
                                folderzip?.file(
                                    BulkValue.placeholderPDFAuditFilename !==
                                        null
                                        ? BulkValue.placeholderPDFAuditFilename
                                        : `${BulkValue.actions.uuid}.pdf`,
                                    dataURLtoBlob(BulkValue.pdfAuditFile)
                                );
                            }
                        }

                        zip.generateAsync({ type: 'blob' }).then((content) => {
                            // see FileSaver.js
                            FileSaver.saveAs(
                                content,
                                'Downloaded Audits Cross Check Data'
                            );
                        });
                    }
                    resolve();
                }),
            []
        );
        const downloadArchiveDraftForActionPreferred = React.useCallback(
            (valueWalk: BulkWalk): Promise<void> =>
                new Promise((resolve) => {
                    if (valueWalk) {
                        const BulkWalked: BulkWalk = valueWalk;
                        const bulkWalkedKey = Object.keys(BulkWalked);
                        // create new zip
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const zip = new JSZip();

                        // eslint-disable-next-line no-plusplus
                        for (
                            let index = 0;
                            index < bulkWalkedKey.length;
                            // eslint-disable-next-line no-plusplus
                            index++
                        ) {
                            const key = bulkWalkedKey[index];

                            const BulkValue = BulkWalked[key];
                            const folderzip = zip.folder(BulkValue.placeholder);

                            if (
                                BulkValue.preferred &&
                                Array.isArray(BulkValue.preferred) &&
                                BulkValue.preferred.length > 0
                            ) {
                                BulkValue.preferred.forEach((el, i) => {
                                    const { placeholder, file } =
                                        BulkValue.preferred[i];

                                    folderzip?.file(
                                        placeholder,
                                        dataURLtoBlob(file)
                                    );
                                });
                            }
                        }
                        if (Object.keys(zip.files).length > 0) {
                            zip.generateAsync({ type: 'blob' }).then(
                                (content) => {
                                    // see FileSaver.js
                                    FileSaver.saveAs(
                                        content,
                                        'Downloaded Preferred Cross check Data'
                                    );
                                }
                            );
                        }
                    }
                    resolve();
                }),
            []
        );
        const handleBulkDocumentsDownload = React.useCallback(async () => {
            if (bulkData) {
                setGenerattingZip(() => true);
                handleProgressBarReset();
                await createArchiveDraftInLocalStorage(
                    bulkData,
                    'documents',
                    (index) => {
                        setArrayFailureDownloads((prev) => {
                            const prevValue = prev;
                            prevValue[index] = false;
                            return prevValue;
                        });
                    },
                    (index) => {
                        setArrayFailureDownloads((prev) => {
                            const prevValue = prev;
                            prevValue[index] = true;
                            return prevValue;
                        });
                    }
                ).then(async (bulkDrafted) => {
                    await walkArchiveDraftInLocalStorage(
                        bulkDrafted,
                        (index) => {
                            setArrayFailureDownloads((prev) => {
                                const prevValue = prev;
                                prevValue[index] = false;
                                return prevValue;
                            });
                            setArraySuccessDownloads((prev) => {
                                const prevValue = prev;
                                prevValue[index] = true;
                                return prevValue;
                            });
                        },
                        (index) => {
                            setArrayFailureDownloads((prev) => {
                                const prevValue = prev;
                                prevValue[index] = true;
                                return prevValue;
                            });
                        }
                    ).then(async (BulkWalk) => {
                        await downloadArchiveDraftForActionDownloads(
                            BulkWalk
                        ).then(() => {
                            setGenerattingZip(false);
                        });
                    });
                });
            }
        }, [
            bulkData,
            createArchiveDraftInLocalStorage,
            downloadArchiveDraftForActionDownloads,
            handleProgressBarReset,
            walkArchiveDraftInLocalStorage,
        ]);
        const handleBulkReportDownload = React.useCallback(() => {
            if (bulkData) {
                setDownloading(true);
                handleProgressBarReset();
                createArchiveDraftInLocalStorage(
                    bulkData,
                    'reports',
                    (index) => {
                        setArrayFailureReports((prev) => {
                            const prevValue = prev;
                            prevValue[index] = false;
                            return prevValue;
                        });
                    },
                    (index) => {
                        setArrayFailureReports((prev) => {
                            const prevValue = prev;
                            prevValue[index] = true;
                            return prevValue;
                        });
                    }
                ).then((bulkDrafted) => {
                    walkArchiveDraftInLocalStorage(
                        bulkDrafted,
                        (index) => {
                            setArrayFailureReports((prev) => {
                                const prevValue = prev;
                                prevValue[index] = false;
                                return prevValue;
                            });
                            setArraySuccessReports((prev) => {
                                const prevValue = prev;
                                prevValue[index] = true;
                                return prevValue;
                            });
                        },
                        (index) => {
                            setArrayFailureReports((prev) => {
                                const prevValue = prev;
                                prevValue[index] = true;
                                return prevValue;
                            });
                        }
                    ).then((BulkWalk) => {
                        downloadArchiveDraftForActionReports(BulkWalk).then(
                            () => {
                                setDownloading(false);
                            }
                        );
                    });
                });
            }
        }, [
            bulkData,
            createArchiveDraftInLocalStorage,
            downloadArchiveDraftForActionReports,
            handleProgressBarReset,
            walkArchiveDraftInLocalStorage,
        ]);

        const handleBulkAuditDownload = React.useCallback(() => {
            if (bulkData) {
                setDownloadingAudit(true);
                handleProgressBarReset();
                createArchiveDraftInLocalStorage(
                    bulkData,
                    'audit',
                    (index) => {
                        setArrayFailureAudits((prev) => {
                            const prevValue = prev;
                            prevValue[index] = false;
                            return prevValue;
                        });
                    },
                    (index) => {
                        setArrayFailureAudits((prev) => {
                            const prevValue = prev;
                            prevValue[index] = true;
                            return prevValue;
                        });
                    }
                ).then((bulkDrafted) => {
                    walkArchiveDraftInLocalStorage(
                        bulkDrafted,
                        (index) => {
                            setArrayFailureAudits((prev) => {
                                const prevValue = prev;
                                prevValue[index] = false;
                                return prevValue;
                            });
                            setArraySuccessAudits((prev) => {
                                const prevValue = prev;
                                prevValue[index] = true;
                                return prevValue;
                            });
                        },
                        (index) => {
                            setArrayFailureAudits((prev) => {
                                const prevValue = prev;
                                prevValue[index] = true;
                                return prevValue;
                            });
                        }
                    ).then((BulkWalk) => {
                        downloadArchiveDraftForActionAudits(BulkWalk).then(
                            () => {
                                setDownloadingAudit(false);
                            }
                        );
                    });
                });
            }
        }, [
            bulkData,
            createArchiveDraftInLocalStorage,
            downloadArchiveDraftForActionAudits,
            handleProgressBarReset,
            walkArchiveDraftInLocalStorage,
        ]);

        const handleBulkPreferredDownload = React.useCallback(() => {
            if (bulkData) {
                setDownloadingPreferred(true);
                handleProgressBarReset();
                createArchiveDraftInLocalStorage(
                    bulkData,
                    'preferred',
                    (index) => {
                        setArrayFailurePreferred((prev) => {
                            const prevValue = prev;
                            prevValue[index] = false;
                            return prevValue;
                        });
                    },
                    (index) => {
                        setArrayFailurePreferred((prev) => {
                            const prevValue = prev;
                            prevValue[index] = true;
                            return prevValue;
                        });
                    }
                ).then((bulkDrafted) => {
                    walkArchiveDraftInLocalStorage(
                        bulkDrafted,
                        (index) => {
                            setArrayFailurePreferred((prev) => {
                                const prevValue = prev;
                                prevValue[index] = false;
                                return prevValue;
                            });
                            setArraySuccessPreferred((prev) => {
                                const prevValue = prev;
                                prevValue[index] = true;
                                return prevValue;
                            });
                        },
                        (index) => {
                            setArrayFailurePreferred((prev) => {
                                const prevValue = prev;
                                prevValue[index] = true;
                                return prevValue;
                            });
                        }
                    ).then((BulkWalk) => {
                        downloadArchiveDraftForActionPreferred(BulkWalk).then(
                            () => {
                                setDownloadingPreferred(false);
                            }
                        );
                    });
                });
            }
        }, [
            bulkData,
            createArchiveDraftInLocalStorage,
            downloadArchiveDraftForActionPreferred,
            handleProgressBarReset,
            walkArchiveDraftInLocalStorage,
        ]);

        const handleDetermineProgressBar = React.useCallback(
            (index: number) => {
                if (
                    ((arrayDataFailureAudtis.some(
                        (el, i) => i === index && el === true
                    ) ||
                        (arrayDataSuccessAudits.some(
                            (el, i) => i === index && el === true
                        ) &&
                            isDownloadingAudit) ||
                        isDownloadingAudit === false) &&
                        (((arrayDataFailureDownloads.some(
                            (el, i) => i === index && el === true
                        ) ||
                            arrayDataSuccessDownloads.some(
                                (el, i) => i === index && el === true
                            )) &&
                            isGeneratingZip) ||
                            isGeneratingZip === false) &&
                        (((arrayDataFailureReports.some(
                            (el, i) => i === index && el === true
                        ) ||
                            arrayDataSuccessReports.some(
                                (el, i) => i === index && el === true
                            )) &&
                            isDownloading) ||
                            isDownloading === false) &&
                        (((arrayDataFailurePreferred.some(
                            (el, i) => i === index && el === true
                        ) ||
                            arrayDataSuccessPreferred.some(
                                (el, i) => i === index && el === true
                            )) &&
                            isDownloadingPreferred) ||
                            isDownloadingPreferred === false)) ||
                    isProgress === false
                ) {
                    return 'determinate';
                }

                return 'indeterminate';
            },
            [
                arrayDataFailureAudtis,
                arrayDataFailureDownloads,
                arrayDataFailurePreferred,
                arrayDataFailureReports,
                arrayDataSuccessAudits,
                arrayDataSuccessDownloads,
                arrayDataSuccessPreferred,
                arrayDataSuccessReports,
                isDownloading,
                isDownloadingAudit,
                isDownloadingPreferred,
                isGeneratingZip,
                isProgress,
            ]
        );
        const handleDetermineProgressColor = React.useCallback(
            (index: number) => {
                if (isProgress === false) {
                    return 'secondary';
                }
                if (
                    arrayDataFailureAudtis.some(
                        (el, i) => i === index && el === true
                    ) ||
                    arrayDataFailureDownloads.some(
                        (el, i) => i === index && el === true
                    ) ||
                    arrayDataFailurePreferred.some(
                        (el, i) => i === index && el === true
                    ) ||
                    arrayDataFailureReports.some(
                        (el, i) => i === index && el === true
                    )
                ) {
                    return 'warning';
                }
                if (
                    ((arrayDataSuccessAudits.some(
                        (el, i) => i === index && el === true
                    ) &&
                        isDownloadingAudit) ||
                        isDownloadingAudit === false) &&
                    ((arrayDataSuccessDownloads.some(
                        (el, i) => i === index && el === true
                    ) &&
                        isGeneratingZip) ||
                        isGeneratingZip === false) &&
                    ((arrayDataSuccessPreferred.some(
                        (el, i) => i === index && el === true
                    ) &&
                        isDownloadingPreferred) ||
                        isDownloadingPreferred === false) &&
                    ((arrayDataSuccessReports.some(
                        (el, i) => i === index && el === true
                    ) &&
                        isDownloading) ||
                        isDownloading === false)
                ) {
                    return 'success';
                }
                return 'info';
            },
            [
                arrayDataFailureAudtis,
                arrayDataFailureDownloads,
                arrayDataFailurePreferred,
                arrayDataFailureReports,
                arrayDataSuccessAudits,
                arrayDataSuccessDownloads,
                arrayDataSuccessPreferred,
                arrayDataSuccessReports,
                isDownloading,
                isDownloadingAudit,
                isDownloadingPreferred,
                isGeneratingZip,
                isProgress,
            ]
        );
        const getBulkTextualProgress = React.useCallback(
            (index: number) => {
                let sum = 0;
                if (
                    arrayDataSuccessReports.some(
                        (el, i) => i === index && el === true
                    ) &&
                    arrayDataFailureReports.some(
                        (el, i) => i === index && el === false
                    )
                ) {
                    sum += 1;
                }
                if (
                    arrayDataSuccessAudits.some(
                        (el, i) => i === index && el === true
                    ) &&
                    arrayDataFailureAudtis.some(
                        (el, i) => i === index && el === false
                    )
                ) {
                    sum += 1;
                }
                if (
                    arrayDataSuccessDownloads.some(
                        (el, i) => i === index && el === true
                    ) &&
                    arrayDataFailureDownloads.some(
                        (el, i) => i === index && el === false
                    )
                ) {
                    sum += 1;
                }
                if (
                    arrayDataSuccessPreferred.some(
                        (el, i) => i === index && el === true
                    ) &&
                    arrayDataFailurePreferred.some(
                        (el, i) => i === index && el === false
                    )
                ) {
                    sum += 1;
                }
                const downloadCounts = downloadCount.filter(
                    (e) => e === true
                ).length;
                return `${sum}/${downloadCounts}`;
            },
            [
                arrayDataFailureAudtis,
                arrayDataFailureDownloads,
                arrayDataFailurePreferred,
                arrayDataFailureReports,
                arrayDataSuccessAudits,
                arrayDataSuccessDownloads,
                arrayDataSuccessPreferred,
                arrayDataSuccessReports,
                downloadCount,
            ]
        );

        return (
            <Box>
                <Modal
                    disableEscapeKeyDown={
                        isDownloading ||
                        isDownloadingAudit ||
                        isDownloadingPreferred ||
                        isGeneratingZip
                    }
                    open={
                        (open && arrayData !== null && isLoading === false) ||
                        areWeTestingWithJest()
                    }
                    onClose={handleClose}
                    aria-labelledby="parent-modal-title"
                    aria-describedby="parent-modal-description"
                >
                    {arrayData !== null ? (
                        <Box className="container-bulk-modal">
                            <Box className="justify-space-between align-center">
                                <Typography variant="h3" className="title">
                                    Bulk Downloading
                                </Typography>
                                <IconButton
                                    edge="end"
                                    onClick={handleClose}
                                    title="close"
                                >
                                    <CloseIcon className="close-icon" />
                                </IconButton>
                            </Box>

                            {false && (
                                <Box>
                                    <IconButton
                                        onClick={handleClose}
                                        aria-label="Example"
                                        className="bulk-container-close"
                                        title="close"
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                    <Typography
                                        variant="h2"
                                        id="parent-modal-title"
                                    >
                                        Bulk Downloading
                                    </Typography>
                                </Box>
                            )}

                            <Box className="container-bulk-detail">
                                <Grid
                                    container
                                    className="bulk-modal-description"
                                >
                                    {arrayData.map((el, i) => (
                                        <>
                                            <Grid
                                                item
                                                xs={6}
                                                className="bulk-label-data"
                                            >
                                                <Typography className="packet-data-label bulk-data-typo">
                                                    {`Cross Check ${i + 1}`}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                item
                                                xs={6}
                                                className="bulk-content-data"
                                            >
                                                <Stack
                                                    direction="column"
                                                    spacing={1}
                                                >
                                                    <Typography
                                                        className="packet-data bulk-data-typo"
                                                        data-testid="client"
                                                    >
                                                        {el}
                                                    </Typography>
                                                    <Stack direction="column">
                                                        <LinearProgress
                                                            color={handleDetermineProgressColor(
                                                                i
                                                            )}
                                                            variant={handleDetermineProgressBar(
                                                                i
                                                            )}
                                                            value={100}
                                                        />
                                                        <Typography className="bulk-data-typo">
                                                            {getBulkTextualProgress(
                                                                i
                                                            )}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            </Grid>
                                        </>
                                    ))}
                                </Grid>
                            </Box>
                            <Grid
                                container
                                spacing={2}
                                className="container-bulk-button mt-1"
                            >
                                <Grid item xs={12}>
                                    <AppPrimaryButton
                                        title="Download documents"
                                        fullWidth
                                        className="btn-small download"
                                        isLoading={isGeneratingZip}
                                        loadingSize={22}
                                        disabled={isGeneratingZip}
                                        onClick={handleBulkDocumentsDownload}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <AppPrimaryButton
                                        title="Download Report"
                                        fullWidth
                                        className="btn-small download"
                                        isLoading={isDownloading}
                                        loadingSize={22}
                                        disabled={isDownloading}
                                        onClick={handleBulkReportDownload}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <AppPrimaryButton
                                        title="Download Audit"
                                        fullWidth
                                        className="btn-small download"
                                        isLoading={isDownloadingAudit}
                                        loadingSize={22}
                                        disabled={isDownloadingAudit}
                                        onClick={handleBulkAuditDownload}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <AppPrimaryButton
                                        testid="download-report"
                                        fullWidth
                                        title="Download Preferred Documents"
                                        className="btn-small download zip"
                                        isLoading={isDownloadingPreferred}
                                        loadingSize={22}
                                        disabled={isDownloadingPreferred}
                                        onClick={handleBulkPreferredDownload}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    ) : (
                        <div />
                    )}
                </Modal>
            </Box>
        );
    }
);

export default BulkModal;
